import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import { Box, Tooltip, Typography } from '@mui/material';
import { isControlState } from '../lib/controlStates';
import { colors } from '../theme';

// Small "Control" chip rendered beside a state code when that state
// is one of the 17 US Alcohol Beverage Control states. License-state
// codes render nothing — the absence of a chip is the visual signal
// that the rep is working through a distributor pathway.

interface ControlStateChipProps {
  stateCode: string | null | undefined;
  /** Drop the icon for ultra-tight contexts (table column headers, etc.). */
  compact?: boolean;
}

export function ControlStateChip({ stateCode, compact = false }: ControlStateChipProps) {
  if (!isControlState(stateCode)) return null;
  const color = '#7c3aed'; // purple — distinct from premises badge palette
  return (
    <Tooltip
      placement="top"
      arrow
      title={
        <Box sx={{ maxWidth: 260 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color, mb: 0.25 }}>
            Control state
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: colors.textSecondary, lineHeight: 1.5 }}>
            The state holds a wholesale or retail monopoly on distilled spirits. Selling here goes
            through the state liquor board / authority, not independent distributors.
          </Typography>
        </Box>
      }
      slotProps={{
        tooltip: {
          sx: {
            bgcolor: '#fff',
            border: `1px solid ${colors.border}`,
            borderRadius: 1.5,
            px: 1.75,
            py: 1.25,
            boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
            maxWidth: 280,
          },
        },
        arrow: {
          sx: { color: '#fff', '&::before': { border: `1px solid ${colors.border}` } },
        },
      }}
    >
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.3,
          fontSize: 9,
          fontWeight: 800,
          letterSpacing: '0.08em',
          px: compact ? 0.45 : 0.6,
          py: 0.15,
          borderRadius: 0.75,
          color,
          bgcolor: `${color}14`,
          border: `1px solid ${color}40`,
          cursor: 'help',
          flexShrink: 0,
          lineHeight: 1.4,
          transition: 'transform 0.12s',
          '&:hover': { transform: 'translateY(-1px)' },
        }}
      >
        {!compact && <GavelOutlinedIcon sx={{ fontSize: 10 }} />}
        CTRL
      </Box>
    </Tooltip>
  );
}
